import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"

import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Icon,
  Input,
  Label,
  Loader,
  Menu,
  Message,
  Popup,
} from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"
import type { OpenAPI } from "simplydo/interfaces"
import { LabelChooser } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser"
import styled from "styled-components"
import uuid from "uuid"

import actions from "actions"
import api from "api"
import { ChallengeChooser, UserChooser } from "components/lib/Choosers"
import { AssessorAudienceWarning } from "components/lib/Choosers/UserChooser/Content/ChallengeAudienceWarning"
import ConfirmAnnouncementModal from "components/lib/ConfirmAnnouncementModal"
import ChallengeIdeaFilters, { ChallengeFilterKeys } from "components/lib/Filters/ChallengeIdeaFilters"
import { GatedChallengeModal } from "components/lib/GatedChallengeModal"
import { GroupUserManagementModal } from "components/lib/GroupUserManagementModal"
import { confirmMerge } from "components/lib/IdeaMerge"
import IdeaPdfExport from "components/lib/IdeaPdfExport"
import { HandledIdeaPreview as IdeaPreview } from "components/lib/Ideas"
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper"
import ProductTour from "components/lib/ProductTour"
import ProjectBoard from "components/lib/ProjectBoard"
import BoardEvents from "components/lib/ProjectBoard/BoardEvents"
import { EmptyBox } from "components/lib/UI"
import { type ChallengesReducer, defaultInitialState as challengeReducerDefaultState } from "reducers/challenges"
import { useAppDispatch, useAppSelector } from "store"
import useTheme from "theme/useTheme"
import { useDidMount } from "utils/useDidMount"
import util from "utils/utils"

import {
  addTag as addTagAction,
  bulkRemoveAssessors as bulkRemoveAssessorsAction,
  bulkUpdateAssessors,
  bulkUpdateStatus,
  IdeaDeadlineModal,
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  labelIdea as labelIdeaAction,
  Pagination,
  unTag as unTagAction,
  updateIdea,
  updateIdea as updateIdeaAction,
  voteForIdea as voteForIdeaAction,
} from "./IdeaActions"
import IdeaCard from "./IdeaCard"
import ChallengeMyIdeas from "./MyIdeas"
import { ActionPopup, IdeaListContainer } from "./Styles"

const StyledIdeasContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

const StyledIdeaItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 100%;
  flex: 1;
`

const FilterContainer = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column-reverse" : "row")};
  align-items: ${({ theme }) => (!theme.sizes.isMobile ? "flex-start" : "center")};
  justify-content: space-between;
  margin-bottom: 5px;
`

const StyledFilters = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: center;
  gap: 10px;
  .ui.button,
  a,
  .ui.dropdown {
    min-width: fit-content;
    ${({ theme }) =>
      theme.sizes.isMobile &&
      `
      margin-bottom: 5px;
      width: 100%;
    `}
  }
  .ui.button,
  .ui.dropdown {
    height: 33px;
  }
  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
    width: 100%;
  `}
  ${({ theme }) =>
    !theme.sizes.isLargeComputer &&
    `
    margin-bottom: 5px;
  `}
`

const IdeaViewTypes = [
  {
    key: "card",
    text: "Card",
    value: "card",
    icon: "vcard",
  },
  {
    key: "list",
    text: "List",
    value: "list",
    icon: "list",
  },
  {
    key: "board",
    text: "Board",
    value: "board",
    icon: "trello",
  },
]

type IdeasProps = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"]
  newIdea: () => void
  allLabels: any[]
  updateAllLabels: Dispatch<SetStateAction<any[]>>
  popularTags: OpenAPI.Schemas["Tag"][]
  updatePopularTags: Dispatch<SetStateAction<any[]>>
  availableGroups: OpenAPI.Schemas["Group"][]
  setAvailableGroups: Dispatch<SetStateAction<any[]>>
  availableOrganisations: OpenAPI.Schemas["Organisation"][]
  setAvailableOrganisations: Dispatch<SetStateAction<any[]>>
  toggleLabel: (label: any) => void
}

const Ideas = (props: IdeasProps) => {
  const {
    challenge,
    newIdea,
    allLabels,
    updateAllLabels,
    popularTags,
    updatePopularTags,
    availableGroups,
    setAvailableGroups,
    availableOrganisations,
    setAvailableOrganisations,
    toggleLabel,
  } = props
  const [actionPopupOpen, setActionPopupOpen] = useState(false)

  const dispatch = useAppDispatch()

  const onFollow = useCallback(
    (context: string, id: string) => {
      dispatch(actions.user.onFollow(context, id))
    },
    [dispatch],
  )

  const updateFilters = useCallback(
    (filters: Partial<ChallengesReducer>) => {
      dispatch(actions.challenges.updateIdeaFilters(filters))
    },
    [dispatch],
  )

  const setIdeaViewType = useCallback(
    (viewType: string, resetParams = true) => {
      if (resetParams) {
        // Remove view type from query params
        const params = new SearchParams(window.location.search)
        params.delete("queryView")
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`)
      }
      if (["card", "list"].includes(viewType) && util.localStorageIsSupported()) {
        localStorage.setItem("ideaViewTypePreference", viewType)
      }
      dispatch(actions.challenges.setIdeaViewType(viewType))
    },
    [dispatch],
  )

  const updateTagList = useCallback((id: string, tagList: OpenAPI.Schemas["Tag"][]) => {
    api.queryClient.setQueryData(["challenges", id], (prevChallenge: any) => ({
      ...(prevChallenge || {}),
      tags: tagList,
    }))
  }, [])

  const updateDefaultLane = useCallback(
    (updatedLane) => {
      api.queryClient.setQueryData(["challenges", challenge?._id], (prevChallenge: any) => ({
        ...(prevChallenge || {}),
        projectBoard: {
          ...(prevChallenge?.projectBoard || {}),
          defaultLane: updatedLane,
        },
      }))
    },
    [challenge?._id],
  )

  const { t } = useTranslation()
  const navigate = useNavigate()

  const user: OpenAPI.GET<"/users/me">["response"] | undefined = useAppSelector((state) => state.user)
  const hasUser: boolean = !!user
  const { ownerOrganisation } = user ?? {}
  const ideaMergeEnabled = ownerOrganisation?.enabledFeatures?.includes("ideaMerging")
  const canMakeIdea = util.canCreateIdea(user, challenge)
  const coverImageEnabled = !challenge?.preventIdeaCoverImages

  const challengesReducer: ChallengesReducer = useAppSelector((state) => state.challenges)
  const {
    tagFilter,
    ideaFilter: query,
    ideaIncludes: filter,
    ideaOrder: sort,
    ideaOrderDirection: sortDirection,
    ideaPage: page,
    labelsFilter: labels,
    ideaLimit: limit,
    ideaFieldChoices: fieldChoices,
    projectBoardLanesFilter,
    postFetchPreviewIndex,
    groupFilter,
    orgFilter,
    projectBoardFilter,
    ideaViewType,
    adminHasCommented,
    filtersVisible,
    impactFilter,
  } = challengesReducer

  const [ideaState, setIdeaState] = useState({
    ideas: [],
    laneImpactMeasures: {},
    nextPageAvailable: false,
    previousPageAvailable: false,
    total: 0,
    basicLoading: true,
    loading: false,
  })
  const [showMyIdeas, setShowMyIdeas] = useState(true)

  const setIdeasLoading = useCallback(
    (isLoading) => setIdeaState((prevState) => ({ ...prevState, loading: isLoading })),
    [],
  )
  const setIdeasBasicLoading = useCallback(
    (isLoading) => setIdeaState((prevState) => ({ ...prevState, basicLoading: isLoading })),
    [],
  )

  const [ideasVotedFor, setIdeasVotedFor] = useState(0)
  const [challengeChooserIdea, setChallengeChooserIdea] = useState("")
  const [selectedIdeas, setSelectedIdeas] = useState([])
  const [assessmentAssignIdeas, setAssessmentAssignIdeas] = useState([])
  const [assessmentAssignRoundId, setAssessmentAssignRoundId] = useState("")
  const [projectAssignIdeas, setProjectAssignIdeas] = useState([])
  const [nextPhaseChallenge, setNextPhaseChallenge] = useState([])
  const [labelChooserIdea, setLabelChooserIdea] = useState("")
  const [deletingIdeas, setDeletingIdeas] = useState([])
  const [deadlineIdeas, setDeadlineIdeas] = useState([])
  const [managementDeadlineIdeas, setManagementDeadlineIdeas] = useState([])
  const [moveLaneIdeas, setMoveLaneIdeas] = useState([])
  const [groupAddIdeas, setGroupAddIdeas] = useState([])
  const [invitations, setInvitations] = useState([])
  const [previewId, setPreviewId] = useState("")
  const [availableSortParameters, setAvailableSortParameters] = useState({})
  const [availableFilterParameters, setAvailableFilterParameters] = useState({})
  const [availableImpacts, setAvailableImpacts] = useState<OpenAPI.Schemas["OrganisationImpact"][]>([])
  const [allProjectAssignees, setAllProjectAssignees] = useState<OpenAPI.Schemas["User"][]>([])
  const [removingIdeaFromMerge, setRemovingIdeaFromMerge] = useState<string>("")
  const [confirmAnnouncementModalOpen, setConfirmAnnouncementModalOpen] = useState(false)
  const [laneAnnouncements, setLaneAnnouncements] = useState<Array<OpenAPI.Schemas["Announcement"]>>([])
  const [laneUpdateContext, setLaneUpdateContext] = useState<{
    ideaIds?: string[]
    ideaId?: string
    laneId?: string
  }>()
  const ideaFetchId = useRef<string>()

  const [allChallengeProjectLanes, setAllChallengeProjectLanes] = useState([])
  const [selectingMultipleCards, setSelectingMultipleCards] = useState(false)

  const challengeId = challenge?._id
  const { ideas, total, basicLoading, loading, nextPageAvailable, previousPageAvailable, laneImpactMeasures } =
    ideaState
  const theme = useTheme()

  const params = new SearchParams(window.location.search)
  const paramIdeaId = params.get("idea")
  const paramViewType = params.get("queryView")

  useEffect(() => {
    setIdeaState((prevState) => ({ ...prevState, basicLoading: true }))
    if (paramViewType && IdeaViewTypes.map((v) => v.value).includes(paramViewType)) {
      setIdeaViewType(paramViewType, false)
    }
  }, [paramViewType, setIdeaViewType])

  // For project board view, we only want to paginate within the lane of the currently previewed idea
  const paginatedIdeas = useMemo(() => {
    if (ideaViewType !== "board") return ideas
    if (!previewId) return ideas
    const previewIdea = ideas.find((i) => i._id === previewId)
    const currentLaneId = previewIdea?.projectManagement?.boards?.find((b) => b.forId === challengeId)?.lane
    const ideasInLane = ideas.filter(
      (i) => i.projectManagement?.boards?.find((b) => b.forId === challengeId)?.lane === currentLaneId,
    )
    // Now sort by order in lane
    const sortedIdeasInLane = ideasInLane.sort((a, b) => {
      const aOrder = a.projectManagement?.boards?.find((b) => b.forId === challengeId)?.order
      const bOrder = b.projectManagement?.boards?.find((b) => b.forId === challengeId)?.order
      return (aOrder || 0) - (bOrder || 0)
    })
    return sortedIdeasInLane
  }, [ideaViewType, ideas, previewId, challengeId])

  const canGoPrevIdea = useMemo(
    () => paginatedIdeas.findIndex((i) => i._id === previewId) > 0 || previousPageAvailable,
    [paginatedIdeas, previousPageAvailable, previewId],
  )
  const canGoNextIdea = useMemo(
    () => paginatedIdeas.findIndex((i) => i._id === previewId) !== paginatedIdeas.length - 1 || nextPageAvailable,
    [paginatedIdeas, nextPageAvailable, previewId],
  )

  const currentPreviewIdInList = useMemo(() => ideas.some((i) => i._id === previewId), [ideas, previewId])
  const template = challenge?.ownerIdeaTemplate
  const assessmentRounds = template?.assessments?.rounds || []

  const listView = useMemo(() => ideaViewType === "list", [ideaViewType])
  const { ideaFilter } = useAppSelector((state) => state.challenges)
  const filterDebounce = useRef<ReturnType<typeof setTimeout>>()
  const updateSearchTerm = useCallback(
    (term) => {
      updateFilters({ ideaFilter: term, ideaPage: 1 })
    },
    [updateFilters],
  )

  const canManageChallenge = useMemo(() => util.canManageChallenge(user, challenge), [user, challenge])
  const canActionIdeas = useMemo(() => util.canActionIdeas(user, challenge), [user, challenge])
  const canViewProjectBoard = useMemo(
    () => util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id),
    [user, challenge],
  )

  useEffect(() => {
    setAssessmentAssignIdeas([])
    setAssessmentAssignRoundId("")
    setChallengeChooserIdea("")
    setSelectedIdeas([])
    setIdeaState((prevState) => ({ ...prevState, ideas: [] }))
    updateFilters({
      ideaIncludes: ideaViewType === "board" ? "submitted" : null,
      ideaLimit: ideaViewType === "board" ? 10000 : ideaViewType === "list" ? 30 : 16,
    })
  }, [ideaViewType, updateFilters])

  const openIdeaPreview = useCallback((ideaId) => {
    setPreviewId(ideaId)
  }, [])

  useDidMount(() => {
    setPreviewId(paramIdeaId)
  })

  useEffect(() => {
    if (ideaViewType !== "board") {
      setSelectingMultipleCards(false)
    }
  }, [ideaViewType])

  const movePreviewIdea = useCallback(
    (amount) => {
      const ideaIndex = paginatedIdeas.findIndex((i) => i._id === previewId)
      if (ideaIndex === -1) return
      const newIndex = ideaIndex + amount
      if (newIndex === -1 && previousPageAvailable) {
        // Set page to (current page - 1)
        // Set postFetchPreviewIndex to (limit - 1)
        updateFilters({ ideaPage: page - 1, postFetchPreviewIndex: limit - 1 })
      } else if (newIndex === ideas.length && nextPageAvailable) {
        // Set page to (current page + 1)
        // Set postFetchPreviewIndex to (0)
        updateFilters({ ideaPage: page + 1, postFetchPreviewIndex: 0 })
      } else {
        const newPreviewId = paginatedIdeas.map((i) => i._id)[newIndex]
        setPreviewId(newPreviewId)
        // Set old previewId as viewed
        setIdeaState((prevState) => ({
          ...prevState,
          ideas: prevState.ideas.map((idea) => {
            if (idea._id !== previewId) return idea
            return {
              ...idea,
              currentUserHasViewed: true,
            }
          }),
        }))
      }
    },
    [ideas, previewId, nextPageAvailable, previousPageAvailable, page, limit, updateFilters, paginatedIdeas],
  )

  const shouldFetchIdeas = useMemo(() => challengeId && hasUser, [challengeId, hasUser])

  useEffect(() => {
    let isMounted = true
    api.challenges.getIdeaFilters(
      challengeId,
      (data: OpenAPI.GET<"/challenges/{id}/ideas/filters">["response"]) => {
        if (!isMounted) return
        updateAllLabels(data.allLabels)
        updatePopularTags(data.popularTags)
        setAvailableGroups(data.availableGroups)
        setAvailableOrganisations(data.availableOrganisations)
        setAvailableFilterParameters(data.availableFilterParameters)
        setAvailableSortParameters(data.availableSortParameters)
        setAvailableImpacts(data.availableImpacts)

        // @ts-ignore
        setAllProjectAssignees(data.allProjectAssignees)
      },
      (err) => {
        toast.error(err.message)
        setIdeasLoading(false)
      },
    )

    return () => {
      isMounted = false
    }
  }, [challengeId, setAvailableGroups, setAvailableOrganisations, setIdeasLoading, updateAllLabels, updatePopularTags])

  const fetchIdeas = useCallback(() => {
    const fetchId = uuid.v4()
    ideaFetchId.current = fetchId

    // Non-users can view this page, but we don't
    // want to load ideas. So don't enter load loop if no user
    if (!shouldFetchIdeas) return
    setIdeasLoading(true)
    setIdeasBasicLoading(true)

    const queryParams = {
      page,
      query,
      tags: tagFilter,
      filter: filter || "all",
      sort,
      sortDirection,
      labels,
      groupFilter,
      orgFilter,
      fieldChoices,
      limit,
      adminHasCommented,
      projectBoardFilter,
      projectBoardLanesFilter,
      impactFilter,
    }

    // Keep track of whether the main query has finished
    let mainFinished = false
    const queryFunc = (handleLoading = false) => {
      if (fetchId && fetchId !== ideaFetchId.current) {
        return
      }
      if (handleLoading) {
        setIdeasLoading(true)
      }
      api.challenges.getIdeas(
        challengeId,
        { ...queryParams, contentType: "full" },
        (data: OpenAPI.GET<"/challenges/{id}/ideas">["response"]) => {
          mainFinished = true
          if (fetchId && fetchId !== ideaFetchId.current) {
            return
          }
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: data.ideas,
            laneImpactMeasures: data.laneImpactMeasures,
            nextPageAvailable: data.nextPageAvailable,
            previousPageAvailable: data.previousPageAvailable,
            total: data.total,
          }))
          setIdeasVotedFor(data.ideasVotedFor)
          setInvitations(data.invitations)
          if (postFetchPreviewIndex !== null) {
            setPreviewId(data.ideas.map((i) => i._id)[postFetchPreviewIndex])
          }
          setIdeasLoading(false)
        },
        (err) => {
          mainFinished = true
          toast.error(err.message)
          setIdeasLoading(false)
        },
      )
    }

    // First kick off the main loader
    queryFunc()

    if (fetchId && fetchId !== ideaFetchId.current) {
      return
    }
    // Then also load the basic version
    api.challenges.getIdeas(
      challengeId,
      { ...queryParams, contentType: "basic" },
      ({ ideas: basicIdeas }) => {
        if (fetchId && fetchId !== ideaFetchId.current) {
          return
        }
        if (mainFinished) {
          return
        }
        setIdeaState((prevState) => ({
          ...prevState,
          ideas: basicIdeas,
          basicLoading: false,
        }))
      },
      (err) => {
        toast.error(err.message)
        setIdeasLoading(false)
        setIdeasBasicLoading(false)
      },
    )
  }, [
    shouldFetchIdeas,
    tagFilter,
    setIdeasLoading,
    setIdeasBasicLoading,
    page,
    query,
    filter,
    sort,
    sortDirection,
    labels,
    groupFilter,
    orgFilter,
    fieldChoices,
    limit,
    adminHasCommented,
    projectBoardFilter,
    projectBoardLanesFilter,
    impactFilter,
    challengeId,
    postFetchPreviewIndex,
  ])

  useEffect(() => {
    if (filterDebounce.current) {
      clearTimeout(filterDebounce.current)
    }
    filterDebounce.current = setTimeout(() => {
      filterDebounce.current = undefined
      fetchIdeas()
    }, 350)
  }, [fetchIdeas])

  const getIdeaAssignees = useCallback(() => {
    api.challenges.getIdeaAssignees(
      challengeId,
      ({ allProjectAssignees: updatedAllProjectAssignees }) => {
        setAllProjectAssignees(updatedAllProjectAssignees)
      },
      () => {},
    )
  }, [challengeId])

  const updateTagListWithTag = useCallback(
    (tag) => {
      if (challenge) {
        const tagList = [...challenge.tagList]
        const index = tagList.findIndex((allTag) => allTag._id === tag._id)
        tagList[index] = tag

        updateTagList(challenge._id, tagList)
      }
    },
    [updateTagList, challenge],
  )

  const onSelectIdea = useCallback((idea) => {
    setSelectedIdeas((prev) => {
      const prevIndex = prev.findIndex((i) => i._id === idea._id)
      if (prevIndex === -1) {
        return [...prev, idea]
      }
      prev.splice(prevIndex, 1)
      return [...prev]
    })
  }, [])

  // Update idea in ideas array using given data
  const onUpdateIdea = useCallback((ideaId, data) => {
    updateIdeaAction(setIdeaState, ideaId, data)
  }, [])

  /* Update idea project info */
  const getChallengeProjectLanes = useCallback(() => {
    api.boards.getProjectLanes(
      challengeId,
      (data) => {
        setAllChallengeProjectLanes(data.lanes)
      },
      () => {},
    )
  }, [challengeId])

  useEffect(() => {
    if (canManageChallenge || canViewProjectBoard) {
      getChallengeProjectLanes()
    }
  }, [getChallengeProjectLanes, canManageChallenge, canViewProjectBoard])

  // Pass array of idea ids and user ids, and remove the users as assessors from these ideas
  const bulkRemoveAssessors = useCallback(
    (ideaIds, userIds, assessmentRoundId) => {
      bulkRemoveAssessorsAction(
        ideaIds,
        challengeId,
        userIds,
        assessmentRoundId,
        setAssessmentAssignIdeas,
        ideas,
        setIdeaState,
      )
    },
    [challengeId, ideas],
  )

  const onRemoveExternalInvitation = useCallback((ideaId, invitation) => {
    const { _id, invitee } = invitation
    setInvitations((prevInvitations) => prevInvitations.filter((i) => i._id !== _id))
    setIdeaState((prevState) => ({
      ...prevState,
      ideas: prevState.ideas.map((idea) => {
        if (idea._id !== ideaId) return idea
        return {
          ...idea,
          invitedExternalAssessors: idea.invitedExternalAssessors.filter((i) => i._id !== invitee),
        }
      }),
    }))
  }, [])

  const pinIdea = useCallback(
    (ideaId, isPinned) => {
      const originalIdeas = Object.assign([], ideas)
      updateIdea(setIdeaState, ideaId, { isPinned })
      api.ideas.updateStatus(
        ideaId,
        { isPinned },
        () => {
          toast.success(
            isPinned
              ? `${t("common:capitalise", { key: "generic.idea" })} pinned`
              : `${t("common:capitalise", { key: "generic.idea" })} unpinned`,
          )
        },
        () => {
          toast.error("Failed to pin idea")
          setIdeaState((prevState) => ({ ...prevState, ideas: originalIdeas }))
        },
      )
    },
    [t, ideas],
  )

  const bulkMoveIdeas = useCallback(
    (ideaIds, challengeId, onComplete = () => {}) => {
      api.ideas.moveToChallenge(
        ideaIds,
        challengeId,
        () => {
          toast.success(`${t("common:capitalise", { key: "generic.ideas" })} moved to ${t("generic.challenge")}`)
          setSelectedIdeas([])
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: prevState.ideas.filter((i) => !ideaIds.includes(i._id)),
          }))
          api.queryClient.invalidateQueries({ queryKey: ["challenges", challengeId, "ideas"] })
          for (const id of ideaIds) {
            api.queryClient.invalidateQueries({ queryKey: ["ideas", id] })
          }
          if (onComplete) onComplete()
        },
        (err) => toast.error(err.message),
      )
    },
    [t],
  )

  // Add label to idea, with choice of whether to apply or remove
  const labelIdea = useCallback(
    (ideaId, label, isApplied) => {
      labelIdeaAction(ideas, ideaId, label, isApplied, toggleLabel, setIdeaState)
    },
    [ideas, toggleLabel],
  )

  const voteForIdea = useCallback(
    (ideaId, addVote) => {
      voteForIdeaAction(setIdeaState, ideaId, ideas, ideasVotedFor, addVote, onFollow)
    },
    [ideas, ideasVotedFor, onFollow],
  )

  const addTag = useCallback(
    (idea, newTagData) => {
      addTagAction(setIdeaState, updateTagListWithTag, ideas, idea, newTagData)
    },
    [ideas, updateTagListWithTag],
  )

  const unTag = useCallback(
    (idea, tag) => {
      unTagAction(setIdeaState, updateTagListWithTag, idea, tag)
    },
    [updateTagListWithTag],
  )

  const createChallengeFromIdea = useCallback(
    (ideaId) => {
      api.challenges.createFromIdea(
        { idea: ideaId },
        (data) => {
          navigate(`/challenges/${data.challenge._id}`)
          toast.success("Challenge created")
        },
        (err) => toast.error(err.message),
      )
    },
    [navigate],
  )

  const removeIdeaFromMerge = useCallback(
    (
      mergeIdeaId: string,
      ideaId: string,
      onComplete = (_mergeIdeaId: string, _ideaId: string) => {},
      onError = (_mergeIdeaId: string, _ideaId: string) => {},
    ) => {
      // If you're removing the 2nd to last idea from a merge, then we delete the merge container, as there'd be no point in having it
      const mergeIdea = ideas.find((i) => i._id === mergeIdeaId)
      const willDeleteMergeContainer = mergeIdea?.children?.length <= 2
      util
        .confirm(
          `Are you sure you want to remove this ${t("generic.idea")} from the merge?`,
          `The ${t("generic.idea")} will not be deleted and will remain in the ${t("generic.challenge")}. You can re-add it to the merge at any time. ${willDeleteMergeContainer ? `The merge container will be deleted as it will only contain one ${t("generic.idea")}.` : ""}`,
        )
        .then(() => {
          setRemovingIdeaFromMerge(ideaId)
          api.ideas.removeIdeaFromMerge(
            mergeIdeaId,
            ideaId,
            () => {
              if (willDeleteMergeContainer) {
                // If we delete the merge container, we get the ID of the idea that wasn't deleted, and change the preview ID to that
                const nonDeletedIdeaId = mergeIdea?.children?.find((c) => c !== ideaId)
                if (nonDeletedIdeaId) {
                  setPreviewId(nonDeletedIdeaId)
                }
              }
              setRemovingIdeaFromMerge("")
              fetchIdeas()
              onComplete(mergeIdeaId, ideaId)
            },
            (err) => {
              toast.error(err.message)
              setRemovingIdeaFromMerge("")
              onError(mergeIdeaId, ideaId)
            },
          )
        })
        .catch(() => {})
    },
    [fetchIdeas, ideas, t],
  )

  const updateProjectLane = useCallback(
    (targetIdea: string, targetLane: string, announcementIds: Array<string>) => {
      api.boards.updateProjectIdea(
        challengeId,
        targetIdea,
        targetLane,
        { order: 0, sendAnnouncements: announcementIds },
        ({ idea: updatedIdea }) => {
          const newLane = allChallengeProjectLanes.find((l) => l._id === targetLane)
          toast.success(
            `${t("common:capitalise", { key: "generic.idea" })} moved to lane ${newLane?.name || "Default"}`,
          )
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: prevState.ideas.map((idea) => {
              if (idea._id !== targetIdea) {
                return idea
              }
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              }
            }),
          }))
          fetchIdeas()
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [challengeId, allChallengeProjectLanes, t, fetchIdeas],
  )

  const updateProjectLaneMulti = useCallback(
    (ideaIds: string[], targetLane: string, announcementIds: Array<string>) => {
      api.boards.updateProjectIdeasMulti(
        challengeId,
        {
          targetLane,
          ideaIds,
          sendAnnouncements: announcementIds,
        },
        ({ updatedIdeas }) => {
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: prevState.ideas.map((idea) => {
              if (!selectedIdeas.map((i) => i._id).includes(idea._id)) {
                return idea
              }
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id)
              const incomingChangedBoard = updatedIdea.projectManagement?.boards?.find(
                (board) => board.forId === challengeId,
              )
              const updatedBoards = idea.projectManagement?.boards.map((board) => {
                if (board.forId !== challengeId) return board
                return {
                  ...board,
                  lane: incomingChangedBoard?.lane,
                  order: incomingChangedBoard?.order,
                  laneObject: incomingChangedBoard?.laneObject,
                }
              })
              return {
                ...idea,
                projectManagement: updatedBoards,
              }
            }),
          }))
          toast.success(
            `Updated lane for ${util.pluralise(ideaIds.length, t("generic.idea"), t("generic.ideas"), true)}`,
          )
          setSelectedIdeas([])
          fetchIdeas()
        },
        (err) => {
          toast.error(`Unable to move ${t("generic.ideas")}. ${err.message}`)
        },
      )
    },
    [challengeId, selectedIdeas, t, fetchIdeas],
  )

  const checkUpdateProjectLane = useCallback(
    (ideaId, laneId) => {
      const newLane = allChallengeProjectLanes.find((l) => l._id === laneId)
      if (!newLane?.announcements) {
        updateProjectLane(ideaId, laneId, [])
      } else {
        setLaneAnnouncements(newLane.announcements)
        setConfirmAnnouncementModalOpen(true)
        setLaneUpdateContext({ laneId, ideaId })
      }
    },
    [updateProjectLane, allChallengeProjectLanes],
  )

  const checkUpdateProjectLaneMulti = useCallback(
    (ideaIds, laneId) => {
      const newLane = allChallengeProjectLanes.find((l) => l._id === laneId)
      if (!newLane?.announcements) {
        updateProjectLaneMulti(ideaIds, laneId, [])
      } else {
        setLaneAnnouncements(newLane.announcements)
        setConfirmAnnouncementModalOpen(true)
        setLaneUpdateContext({ laneId, ideaIds })
      }
    },
    [allChallengeProjectLanes, updateProjectLaneMulti],
  )

  const updateIdeaProjectDeadlineMulti = useCallback(
    (deadline: Date) => {
      const formattedDeadline = moment(deadline, "YYYY-MM-DD").toDate()
      api.boards.updateProjectIdeasDeadline(
        challengeId,
        { deadline: formattedDeadline, ideaIds: selectedIdeas.map((i) => i._id) },
        ({ updatedIdeas }) => {
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: prevState.ideas.map((idea) => {
              if (!updatedIdeas.map((i) => i._id).includes(idea._id)) {
                return idea
              }
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id)
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              }
            }),
          }))
          toast.success(
            `Updated deadline for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          )
        },
        (err) => {
          toast.error(`Unable to update deadline. ${err.message}`)
        },
      )
    },
    [challengeId, selectedIdeas, t],
  )

  const bulkProjectAssign = useCallback(
    (userIds: string[]) => {
      api.boards.bulkAssignProjectIdeas(
        challengeId,
        {
          ideaIds: selectedIdeas.map((i) => i._id),
          userIds,
        },
        ({ updatedIdeas, allAssignees }) => {
          setIdeaState((prevState) => ({
            ...prevState,
            ideas: prevState.ideas.map((idea) => {
              if (!selectedIdeas.map((i) => i._id).includes(idea._id)) {
                return idea
              }
              const updatedIdea = updatedIdeas.find((i) => i._id === idea._id)
              return {
                ...idea,
                projectManagement: updatedIdea.projectManagement,
              }
            }),
          }))
          setSelectedIdeas([])
          setAllProjectAssignees(allAssignees)
          toast.success(
            `Assigned ${util.pluralise(userIds.length, "user", "users", true)} for ${util.pluralise(updatedIdeas.length, t("generic.idea"), t("generic.ideas"), true)}`,
          )
        },
        (err) => {
          toast.error(err.message)
        },
      )
    },
    [challengeId, selectedIdeas, t],
  )

  const RenderIdeaActions = useCallback(
    (ideaIds, isBulk = false) => {
      const usingSingleIdea = isBulk ? null : ideaState.ideas.find((idea) => idea._id === ideaIds[0])
      const usingIdeas = isBulk ? selectedIdeas : [usingSingleIdea]

      // You can only merge ideas, where either:
      // - None of the ideas have been merged
      // - One of the ideas is a merge parent, and the rest of the selected ideas have not been merged/are not merge parents
      const canMerge =
        isBulk &&
        (selectedIdeas.filter((i) => i.parent || i.children).length === 0 ||
          (selectedIdeas.filter((i) => i.children).length === 1 &&
            selectedIdeas.filter((i) => !(i.parent || i.children)).length === selectedIdeas.length - 1))

      if (!challenge) return null
      return (
        <ActionPopup>
          <Menu secondary vertical>
            {ideaViewType === "board" &&
            (canManageChallenge ||
              util.hasPermission(user, "challenge.moveProjectIdeas", challengeId) ||
              util.hasPermission(user, "challenge.editProjectAssignees", challengeId) ||
              util.hasPermission(user, "challenge.editProjectDates", challengeId)) ? (
              <>
                {canManageChallenge || util.hasPermission(user, "challenge.moveProjectIdeas", challengeId) ? (
                  <Dropdown
                    item
                    icon={"move"}
                    text={`Move all ${t("generic.ideas")} to project lane`}
                    onClick={() => {
                      setActionPopupOpen(false)
                      setMoveLaneIdeas(selectedIdeas)
                    }}
                  />
                ) : null}
                {canManageChallenge || util.hasPermission(user, "challenge.editProjectAssignees", challengeId) ? (
                  <Menu.Item
                    content={`Project assign users to all ${t("generic.ideas")}`}
                    onClick={() => {
                      setProjectAssignIdeas(ideaIds)
                      setActionPopupOpen(false)
                    }}
                  />
                ) : null}
                {canManageChallenge || util.hasPermission(user, "challenge.editProjectDates", challengeId) ? (
                  <Menu.Item
                    icon="calendar outline"
                    content="Set management deadline"
                    onClick={() => {
                      setManagementDeadlineIdeas(usingIdeas)
                      setActionPopupOpen(false)
                    }}
                  ></Menu.Item>
                ) : null}
              </>
            ) : null}
            {canManageChallenge || util.hasPermission(user, "challenge.pinIdeas", challengeId) ? (
              <Popup
                on="hover"
                position="right center"
                content={<PinIdeaHelper challenge={challenge} />}
                trigger={
                  <Menu.Item
                    icon="pin"
                    content={isBulk ? "Pin" : usingSingleIdea?.isPinned ? "Unpin" : "Pin"}
                    onClick={() =>
                      !isBulk
                        ? pinIdea(usingSingleIdea._id, !usingSingleIdea?.isPinned)
                        : bulkUpdateStatus(challengeId, ideaIds, setIdeaState, "isPinned", true)
                    }
                  />
                }
              />
            ) : null}
            {canManageChallenge || util.hasPermission(user, "challenge.editIdeaDeadlines", challengeId) ? (
              <Menu.Item
                icon="calendar"
                content="Set submission deadline"
                onClick={() => {
                  setActionPopupOpen(false)
                  setDeadlineIdeas(usingIdeas)
                }}
              />
            ) : null}
            {util.hasPermission(user, "org.addToGroupsDirectly", user?.ownerOrganisation?._id) ? (
              <Menu.Item
                icon="users"
                content="Add authors to group"
                onClick={() => {
                  setActionPopupOpen(false)
                  setGroupAddIdeas(usingIdeas)
                }}
              />
            ) : null}
            <Menu.Item
              content={`Create next-phase ${t("generic.challenge")} from ${t("generic.ideas")}`}
              onClick={() => {
                setActionPopupOpen(false)
                setNextPhaseChallenge(ideaIds)
              }}
              icon="double angle right"
            />
            {ideaMergeEnabled &&
            (canManageChallenge || util.hasPermission(user, "challenge.mergeIdeas", challengeId)) ? (
              <Popup
                on="hover"
                content={
                  canMerge
                    ? `Merge ${t("generic.ideas")} together`
                    : selectedIdeas.length > 1
                      ? `You can only merge ${t("generic.ideas")} where none of the previous ${t("generic.ideas")} have been merged`
                      : `Select at least two ${t("generic.ideas")} to merge`
                }
                trigger={
                  <Menu.Item
                    disabled={selectedIdeas.length < 2 || !canMerge}
                    icon="sitemap"
                    content="Merge"
                    onClick={() => {
                      setActionPopupOpen(false)
                      // @ts-ignore
                      confirmMerge(selectedIdeas, allChallengeProjectLanes, challenge)
                        .then(() => {
                          setSelectedIdeas([])
                          fetchIdeas()
                        })
                        .catch(() => {})
                    }}
                  />
                }
              />
            ) : null}
            {challenge?.ownerIdeaTemplate?.assessmentEnabled &&
            (canManageChallenge || util.hasPermission(user, "challenge.editAssessments", challengeId)) &&
            assessmentRounds.length > 0 ? (
              assessmentRounds.length > 1 ? (
                <Dropdown item icon="user plus" text="Assign assessors" pointing="right">
                  <DropdownMenu
                    style={{
                      right: "100%",
                      left: "auto",
                    }}
                  >
                    {assessmentRounds.map((round) => (
                      <DropdownItem
                        key={round.id}
                        content={`Assign to ${round.name}`}
                        onClick={() => {
                          setActionPopupOpen(false)
                          setAssessmentAssignRoundId(round.id)
                          setAssessmentAssignIdeas(ideaIds)
                        }}
                      />
                    ))}
                  </DropdownMenu>
                </Dropdown>
              ) : (
                <Menu.Item
                  icon="user plus"
                  content="Assign assessors"
                  onClick={() => {
                    setActionPopupOpen(false)
                    setAssessmentAssignRoundId(assessmentRounds[0].id)
                    setAssessmentAssignIdeas(ideaIds)
                  }}
                />
              )
            ) : null}
            {ideaViewType !== "board" &&
            (canManageChallenge || util.hasPermission(user, "challenge.moveIdeasToChallenge", challengeId)) ? (
              <>
                <Menu.Item
                  icon="arrow right"
                  content={`Move to another ${t("generic.challenge")}`}
                  onClick={() => {
                    setActionPopupOpen(false)
                    setChallengeChooserIdea(ideaIds)
                  }}
                />
                {!isBulk && usingSingleIdea?.challengeCreation && (
                  <Menu.Item
                    icon="target"
                    content={`Create ${t("generic.challenge")} from ${t("generic.idea")}`}
                    onClick={() => createChallengeFromIdea(usingSingleIdea._id)}
                  />
                )}
              </>
            ) : null}

            <IdeaPdfExport
              hidePopup
              ideas={usingIdeas}
              challenge={challenge as unknown as OpenAPI.Schemas["Challenge"]}
              trigger={<Menu.Item icon="pdf file outline" content="Export as PDF" />}
            />

            {!isBulk &&
            listView &&
            (canManageChallenge || util.hasPermission(user, "challenge.editIdeaLabels", challengeId)) ? (
              <Menu.Item
                icon="bookmark"
                content="Apply a label"
                onClick={() => setLabelChooserIdea(usingSingleIdea?._id)}
              />
            ) : null}
            {isBulk && <Menu.Item icon="ban" content="Deselect all" onClick={() => setSelectedIdeas([])} />}
            {canManageChallenge || util.hasPermission(user, "challenge.deleteIdeas", challengeId) ? (
              <Popup
                disabled={!(!isBulk && !!usingSingleIdea?.children)}
                content={`Delete this merged ${t("generic.idea")}. None of the contained ${t("generic.ideas")} will be deleted, and they will be returned to the ${t("generic.idea")} list.`}
                trigger={
                  <Menu.Item
                    icon="trash"
                    content={
                      !isBulk && !!usingSingleIdea?.children
                        ? `Delete merged ${t("generic.idea")}`
                        : `Delete ${t("generic.idea")}${!isBulk ? "" : "s"}`
                    }
                    onClick={() => {
                      setActionPopupOpen(false)
                      setDeletingIdeas(usingIdeas)
                    }}
                  />
                }
              />
            ) : null}
          </Menu>
        </ActionPopup>
      )
    },
    [
      challenge,
      ideaViewType,
      allChallengeProjectLanes,
      ideaMergeEnabled,
      selectedIdeas,
      canManageChallenge,
      listView,
      user,
      pinIdea,
      challengeId,
      fetchIdeas,
      createChallengeFromIdea,
      t,
      ideaState.ideas,
      assessmentRounds,
    ],
  )

  const onClosePreview = useCallback(
    (ideaId) => {
      // Set idea as viewed
      setIdeaState((prevState) => ({
        ...prevState,
        ideas: prevState.ideas.map((idea) => {
          if (idea._id !== ideaId) return idea
          return {
            ...idea,
            currentUserHasViewed: true,
          }
        }),
      }))
      if (canManageChallenge || util.hasPermission(user, "challenge.viewProjectAssignees", challenge?._id)) {
        getIdeaAssignees()
      }
    },
    [canManageChallenge, user, challenge?._id, getIdeaAssignees],
  )

  const hasPinnedIdea = useMemo(() => ideas.some((i) => i.isPinned), [ideas])

  const RenderIdeaCard = useCallback(
    (idea) => (
      // @ts-ignore
      <IdeaCard
        fetchIdeas={fetchIdeas}
        key={idea._id}
        idea={idea}
        invitations={invitations}
        ideasVotedFor={ideasVotedFor}
        challenge={challenge}
        addTag={(tag) => addTag(idea, tag)}
        unTag={(tag) => unTag(idea, tag)}
        voteForIdea={voteForIdea}
        listView={listView}
        labelIdea={labelIdea}
        onSelectIdea={onSelectIdea}
        selectedIdeas={selectedIdeas.map((i) => i._id)}
        ideaActions={RenderIdeaActions}
        onUpdateIdea={onUpdateIdea}
        removeAssessors={bulkRemoveAssessors}
        pinIdea={pinIdea}
        setPreviewOpen={openIdeaPreview}
        onRemoveExternalInvitation={onRemoveExternalInvitation}
        hasPinnedIdea={hasPinnedIdea}
        allChallengeProjectLanes={allChallengeProjectLanes}
        updateProjectLane={checkUpdateProjectLane}
        loading={loading}
        basicLoading={basicLoading}
      />
    ),
    [
      fetchIdeas,
      invitations,
      ideasVotedFor,
      challenge,
      voteForIdea,
      listView,
      labelIdea,
      onSelectIdea,
      selectedIdeas,
      RenderIdeaActions,
      onUpdateIdea,
      bulkRemoveAssessors,
      pinIdea,
      openIdeaPreview,
      onRemoveExternalInvitation,
      hasPinnedIdea,
      allChallengeProjectLanes,
      checkUpdateProjectLane,
      loading,
      basicLoading,
      addTag,
      unTag,
    ],
  )

  const usedVotes = parseInt(challenge?.voteLimit, 10) === ideasVotedFor
  const votesRemaining = parseInt(challenge?.voteLimit, 10) - ideasVotedFor
  const toLabelIdea = ideas.find((i) => i._id === labelChooserIdea)

  const isFullSize = !theme.sizes.isMobile

  const activeIdeaViewType = useMemo(
    () => IdeaViewTypes.find((viewType) => viewType.key === ideaViewType),
    [ideaViewType],
  )

  const searchComponent = useMemo(
    () => (
      <Input
        size="small"
        placeholder={
          canManageChallenge || canViewProjectBoard
            ? `${t("challenge.ideas.filter.admin.search")}...`
            : `${t("challenge.ideas.filter.search")}...`
        }
        icon="search"
        value={ideaFilter}
        onChange={(e) => updateSearchTerm(e.target.value)}
        style={{ height: 34, flex: 1, maxWidth: theme.sizes.isMobile ? undefined : 350 }}
        fluid
      />
    ),
    [canManageChallenge, canViewProjectBoard, ideaFilter, theme.sizes.isMobile, updateSearchTerm, t],
  )

  const numberOfFiltersNotDefault = useMemo(
    () =>
      ChallengeFilterKeys.filter((key) => {
        // If we're on the board view, we don't want to count the ideaOrder filter as a filter since we hide the filter option and it can't be changed
        if (ideaViewType === "board" && key === "ideaOrder") {
          return false
        }
        return !util.variablesAreEqual(challengesReducer[key], challengeReducerDefaultState[key])
      }).length,
    [challengesReducer, ideaViewType],
  )

  return (
    <StyledIdeasContainer>
      <ConfirmAnnouncementModal
        modalOpen={confirmAnnouncementModalOpen}
        announcements={laneAnnouncements}
        handleConfirm={(announcementIds) => {
          setConfirmAnnouncementModalOpen(false)
          if (laneUpdateContext.ideaId) {
            updateProjectLane(laneUpdateContext.ideaId, laneUpdateContext.laneId, announcementIds)
          } else {
            updateProjectLaneMulti(laneUpdateContext.ideaIds, laneUpdateContext.laneId, announcementIds)
          }
          setLaneUpdateContext({})
        }}
      />
      <ProductTour
        tour="projectBoardOnIdeasPage"
        isReady={!!challenge && ideaViewType === "board" && window.location.pathname.indexOf("/ideas") > -1}
      />

      <ChallengeIdeaFilters
        // @ts-ignore
        challenge={challenge}
        popularTags={popularTags}
        allLabels={allLabels}
        availableGroups={availableGroups}
        availableOrganisations={availableOrganisations}
        availableSortParameters={availableSortParameters}
        availableFilterParameters={availableFilterParameters}
        allProjectAssignees={allProjectAssignees}
        availableImpacts={availableImpacts}
      />

      <IdeaDeletionModal
        ideasToDelete={deletingIdeas}
        setIdeasToDelete={setDeletingIdeas}
        setIdeaState={setIdeaState}
        setSelectedIdeas={setSelectedIdeas}
      />
      <IdeaDeadlineModal
        deadlineIdeas={deadlineIdeas}
        setDeadlineIdeas={setDeadlineIdeas}
        setIdeaState={setIdeaState}
      />
      <IdeaManagementDeadlineModal
        managementDeadlineIdeas={managementDeadlineIdeas}
        setManagementDeadlineIdeas={setManagementDeadlineIdeas}
        updateIdeaProjectDeadlineMulti={updateIdeaProjectDeadlineMulti}
      />
      <IdeaMoveToLaneModal
        moveLaneIdeas={moveLaneIdeas}
        setMoveLaneIdeas={setMoveLaneIdeas}
        allChallengeProjectLanes={allChallengeProjectLanes}
        checkUpdateProjectLaneMulti={checkUpdateProjectLaneMulti}
        challenge={challenge}
      />
      <GroupUserManagementModal
        users={groupAddIdeas.flatMap((idea) => {
          const userIds = []
          if (idea.user) {
            userIds.push(idea.user)
          }
          if (idea.collaborators) {
            userIds.push(...idea.collaborators)
          }
          return userIds
        })}
        onClose={() => setGroupAddIdeas([])}
      />
      <IdeaPreview
        totalIdeas={paginatedIdeas.length}
        currentIdeaPosition={paginatedIdeas.findIndex((i) => i._id === previewId) + 1}
        ideaId={previewId}
        assessmentEnabled={challenge?.ownerIdeaTemplate?.assessmentEnabled}
        closeModal={() => {
          setPreviewId("")
          onClosePreview(previewId)
        }}
        page={page}
        limit={limit}
        onUpdateIdea={onUpdateIdea}
        moveIdea={currentPreviewIdInList && movePreviewIdea}
        canGoNextIdea={canGoNextIdea}
        canGoPrevIdea={canGoPrevIdea}
        removeIdeaFromMerge={removeIdeaFromMerge}
        removingIdeaFromMerge={removingIdeaFromMerge}
        isProjectBoard={ideaViewType === "board"}
        challenge={challenge}
        lanes={allChallengeProjectLanes}
        updateProjectLane={checkUpdateProjectLane}
      />
      <LabelChooser
        isOpen={!!labelChooserIdea}
        onClose={() => setLabelChooserIdea("")}
        challengeId={challengeId}
        onComplete={(em) => labelIdea(labelChooserIdea, em, true)}
        existing={toLabelIdea?.labels}
        onRemoveExisting={(em) => labelIdea(labelChooserIdea, em, false)}
      />
      {challenge && (
        <GatedChallengeModal
          openModal={nextPhaseChallenge.length > 0}
          closeModal={() => setNextPhaseChallenge([])}
          challengeName={challenge.name}
          challengeId={challenge._id}
          newGroupUsers={selectedIdeas.flatMap((idea) =>
            idea.collaborators ? [idea.user].concat(idea.collaborators) : [idea.user],
          )}
        ></GatedChallengeModal>
      )}
      <UserChooser
        isOpen={projectAssignIdeas.length > 0}
        onClose={() => setProjectAssignIdeas([])}
        onComplete={(users) => bulkProjectAssign(users.map((u) => u._id))}
        forType="projectAssignee"
        forId={challengeId}
        clearOnComplete
        enabledFeatures={{ search: true, invite: false }}
        subtitle={`Choose multiple users to project assign to ${t("generic.idea")}.`}
        confirm="Assign these users"
        searchFunction={(term, cb, fail) =>
          api.boards.searchProjectAssignees(challengeId, term, ({ users }) => cb(users), fail)
        }
      />
      <UserChooser
        isOpen={assessmentAssignIdeas.length > 0}
        onClose={() => {
          setAssessmentAssignRoundId("")
          setAssessmentAssignIdeas([])
        }}
        onComplete={(users) => {
          // @ts-ignore
          const emailUsers = users.filter((u) => u.isEmailInvitee)
          // @ts-ignore
          const existingUsers = users.filter((u) => !u.isEmailInvitee)
          if (emailUsers.length) {
            const ideaDetails = {}
            for (const ideaId of assessmentAssignIdeas) {
              ideaDetails[ideaId] = {
                assessmentRounds: [assessmentAssignRoundId],
              }
            }

            api.invitations.createBulk(
              {
                invitees: emailUsers.map((u) => u._id),
                invitationType: "email",
                forType: "ideaAssessor",
                forId: challengeId,
                forIdeas: assessmentAssignIdeas,
                ideaDetails,
              },
              ({ invitations: newInvitations }) => {
                setInvitations((prevInvitations) => [...prevInvitations, ...newInvitations])
                toast.success(`Invitation${emailUsers.length > 1 ? "s" : ""} sent`)
              },
              () => {},
            )
            setIdeaState((prevState) => ({
              ...prevState,
              ideas: prevState.ideas.map((idea) => {
                if (assessmentAssignIdeas.indexOf(idea._id) === -1) return idea
                return {
                  ...idea,
                  invitedExternalAssessors: [
                    ...(idea?.assessment?.invitedExternalAssessors || []),
                    ...emailUsers.filter((e) => (idea?.assessment?.invitedExternalAssessors || []).indexOf(e) === -1),
                  ],
                }
              }),
            }))
          }
          bulkUpdateAssessors(
            existingUsers,
            challengeId,
            assessmentAssignIdeas,
            assessmentAssignRoundId,
            ideas,
            setIdeaState,
          )
        }}
        forType="assessor"
        forId={challengeId}
        afterContent={() =>
          !challenge.visibility?.organisations?.includes(user.organisation) ? <AssessorAudienceWarning /> : null
        }
        clearOnComplete
        enabledFeatures={{ search: true, invite: true }}
        subtitle={`Choose users to assign as assessors for ${assessmentAssignIdeas.length > 1 ? `the selected ${t("generic.ideas")}` : `the selected ${t("generic.idea")}`}. Assessors will be able to find the ${t("generic.ideas")} on their 'Assessments' tab within this ${t("generic.challenge")}.`}
        confirm="Assign these assessors"
        beforeContent={() => {
          const assessmentRound = assessmentRounds.find((round) => round.id === assessmentAssignRoundId)
          if (!assessmentRound) return null
          const assessmentClosed =
            (assessmentRound.id === "legacy"
              ? challenge?.ownerIdeaTemplate?.assessmentClosed
              : assessmentRound.assessmentClosed) || false
          if (!assessmentClosed) return null

          return (
            <Message warning>
              <Message.Header>Assessment closed</Message.Header>
              <Message.Content>
                Assessment is now closed for this {t("generic.challenge")}. If you assign assessors, they will not be
                able to complete their assessment. You can change this{" "}
                <Link to={`/challenges/${challenge._id}/settings/format`}>here</Link>.
              </Message.Content>
            </Message>
          )
        }}
        searchFunction={(term, cb, fail) =>
          api.search.challengeAssessors(challengeId, term, ({ users }) => cb(users), fail)
        }
        trigger={undefined}
      />
      {challenge?._id && challengeChooserIdea?.length > 0 ? (
        <ChallengeChooser
          isOpen={challengeChooserIdea.length > 0}
          onClose={() => setChallengeChooserIdea("")}
          single
          ignores={[challenge._id]}
          onComplete={(c) => bulkMoveIdeas(challengeChooserIdea, c[0]._id, () => setChallengeChooserIdea(""))}
          beforeContent={`We'll move the selected ${t("generic.idea")}(s) to another ${t("generic.challenge")} that you can manage.`}
          afterContent={
            <Message warning>
              <p>
                <Icon name="warning" />
                <b>Caution</b>:
                {`Moving ${t("generic.ideas")} to another ${t("generic.challenge")} will have the following side effects.`}
              </p>
              <ul>
                <li>
                  The {t("generic.idea")} will be removed from the original {t("generic.challenge")}
                </li>
                <li>
                  The {t("generic.idea")} will keep the {t("generic.idea")} format of the original{" "}
                  {t("generic.challenge")}
                </li>
                <li>{t("common:capitalise", { key: "generic.idea" })} collaborators will be removed</li>
                <li>Assessments will be removed</li>
                <li>Project board assignees will be removed</li>
                <li>Project board notes will be removed</li>
              </ul>
            </Message>
          }
        />
      ) : null}
      {ideaViewType !== "board" ? (
        <ChallengeMyIdeas
          challengeId={challenge?._id}
          style={{ marginBottom: 20 }}
          showMyIdeas={showMyIdeas}
          setShowMyIdeas={setShowMyIdeas}
        />
      ) : null}
      {user ? (
        <div
          style={{
            marginBottom: theme.sizes.isMobile ? 0 : 10,
          }}
        >
          <FilterContainer>
            <StyledFilters style={{ flex: 1 }}>
              {ideaViewType !== "board"
                ? challenge?.voteLimit &&
                  challenge?.votingVisibility === "users" && (
                    <Label>
                      {!usedVotes && <Icon name={"thumbs up"} />} {util.pluralise(votesRemaining, "like", "likes")}{" "}
                      remaining
                    </Label>
                  )
                : null}

              <Button
                primary
                style={{ height: 32, marginRight: 0 }}
                icon="filter"
                size="tiny"
                content={`${theme.sizes.isLargeComputer ? (filtersVisible ? `Hide filters` : `Show filters`) : "Filters"}${numberOfFiltersNotDefault ? ` (${numberOfFiltersNotDefault})` : ""}`}
                onClick={() => updateFilters({ filtersVisible: !filtersVisible })}
              />

              {theme.sizes.isMobile ? null : searchComponent}

              {canManageChallenge || canActionIdeas ? (
                <React.Fragment>
                  {ideaViewType === "board" ? (
                    <Button
                      content={selectingMultipleCards ? "Exit selection mode" : "Select multiple cards"}
                      icon="grab"
                      size="tiny"
                      onClick={() => setSelectingMultipleCards((prev) => !prev)}
                      style={{ width: 180, marginRight: 0 }}
                    />
                  ) : (
                    <Button
                      size="tiny"
                      icon="check"
                      basic
                      content={t("generic.selectAll")}
                      onClick={() =>
                        setSelectedIdeas((prevSelectedIdeas) =>
                          prevSelectedIdeas.concat(
                            ideas.filter((idea) => !prevSelectedIdeas.map((i) => i._id).includes(idea._id)),
                          ),
                        )
                      }
                    />
                  )}
                </React.Fragment>
              ) : null}

              {basicLoading ? <Loader active inline="centered" /> : null}

              {selectedIdeas && selectedIdeas.length > 0 && (
                <Popup
                  on="click"
                  open={actionPopupOpen}
                  onOpen={() => setActionPopupOpen(true)}
                  onClose={() => setActionPopupOpen(false)}
                  trigger={
                    <Button
                      primary
                      icon="dropdown"
                      size="tiny"
                      content={util.pluralise(selectedIdeas.length, t("generic.idea"), t("generic.ideas"))}
                    />
                  }
                  content={RenderIdeaActions(
                    selectedIdeas.map((idea) => idea._id),
                    true,
                  )}
                  style={{ padding: 5 }}
                />
              )}

              {ideaViewType === "board" ? (
                canManageChallenge || util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id) ? (
                  <BoardEvents
                    forId={challengeId}
                    setCurrentIdeaId={setPreviewId}
                    buttonStyle={{ height: 32.5 }}
                    buttonProps={{ size: "tiny" }}
                  />
                ) : null
              ) : null}
            </StyledFilters>

            <StyledFilters>
              <Popup
                on="hover"
                content={`Change ${t("generic.idea")} view`}
                trigger={
                  <Dropdown
                    icon={null}
                    direction="left"
                    trigger={
                      <Button
                        icon={activeIdeaViewType?.icon}
                        content={activeIdeaViewType?.text}
                        size="tiny"
                        style={{
                          flexWrap: "nowrap",
                          display: "flex",
                        }}
                        className="project-board-ideas-page-1"
                      />
                    }
                    options={[
                      !theme.sizes.isLargeComputer &&
                      (util.canManageChallenge(user, challenge) ||
                        util.hasPermission(user, "challenge.editSettings", challengeId))
                        ? {
                            key: "settings",
                            value: "settings",
                            as: Link,
                            to: `/challenges/${challenge?._id}/settings/ideas`,
                            icon: "cogs",
                            text: `View ${t("generic.idea")} settings`,
                          }
                        : null,
                      ...IdeaViewTypes.filter((viewType) => {
                        if (viewType.key === "board") {
                          return (
                            challenge?.projectManagementVisibility === "users" ||
                            canManageChallenge ||
                            util.hasPermission(user, "challenge.viewProjectBoard", challengeId)
                          )
                        }
                        return (
                          !viewType.requiredFeature ||
                          // @ts-ignore
                          user?.ownerOrganisation?.enabledFeatures?.includes(viewType.requiredFeature)
                        )
                      }).map(({ key, text, value, icon }) => ({
                        key,
                        value,
                        icon,
                        text: `${text} view`,
                      })),
                    ].filter((item) => Boolean(item))}
                    onChange={(_e, { value }) => setIdeaViewType(value as string)}
                    selectOnBlur={false}
                  />
                }
              />
            </StyledFilters>
          </FilterContainer>
          {ideaViewType === "board" && filter === "submitted" && theme.sizes.isComputer ? (
            <span
              style={{
                color: "#666",
                fontSize: 12,
              }}
            >
              Draft {t("generic.ideas")} are hidden by default in board view. You can change this in the{" "}
              {t("generic.idea")} filters.
            </span>
          ) : null}
          {theme.sizes.isMobile ? <div style={{ marginBottom: 5, width: "100%" }}>{searchComponent}</div> : null}
        </div>
      ) : null}
      {!user && (
        <Message>
          <h3>Please log in to view this page</h3>
          <p>
            Register or login to your account to track your own {t("generic.ideas")} or to view, comment, and like other{" "}
            {t("generic.ideas")} in this {t("generic.challenge")}.
          </p>
          <Divider section />
          <Button as={Link} to={`/login?then=/challenges/${challenge?._id}/ideas`} primary>
            Login or register
          </Button>
        </Message>
      )}
      {user &&
      (challenge?.ideaVisibility === "users" ||
        canManageChallenge ||
        util.hasPermission(user, "challenge.viewIdeas", challengeId)) ? (
        <StyledIdeaItemsContainer>
          {listView ? (
            <IdeaListContainer>
              <colgroup>
                {/* @ts-ignore */}
                {isFullSize && (canManageChallenge || canActionIdeas) ? <col width="40" height="55" /> : null}
                {/* @ts-ignore */}
                <col width="35" height="55" />
                {coverImageEnabled && <col width="40" />}
                {/* @ts-ignore */}
                {isFullSize ? <col height="55" /> : null}
                <col />
                <col />
              </colgroup>
              <tbody>{ideas.map((idea) => RenderIdeaCard(idea))}</tbody>
            </IdeaListContainer>
          ) : null}
          {ideaViewType === "card" ? (
            <Card.Group itemsPerRow={4} doubling stackable style={{ marginBottom: 20, zIndex: 5 }}>
              {ideas.map((idea) => RenderIdeaCard(idea))}
            </Card.Group>
          ) : null}
          {ideaViewType === "board" ? (
            <ProjectBoard
              forId={challengeId}
              canManageBoard={canManageChallenge}
              canViewBoard={
                canManageChallenge ||
                challenge?.projectManagementVisibility === "users" ||
                util.hasPermission(user, "challenge.viewProjectBoard", challenge?._id)
              }
              loading={loading}
              basicLoading={basicLoading}
              assessmentEnabled={challenge?.ownerIdeaTemplate?.assessmentEnabled}
              defaultLane={challenge?.projectBoard?.defaultLane}
              updateDefaultLane={updateDefaultLane}
              ideas={ideas}
              laneImpactMeasures={laneImpactMeasures}
              fetchIdeas={fetchIdeas}
              setIdeas={(cb: (prevIdeas: OpenAPI.Schemas["Idea"][]) => OpenAPI.Schemas["Idea"][]) =>
                setIdeaState((prevState) => ({ ...prevState, ideas: cb(prevState.ideas) }))
              }
              totalIdeas={total}
              currentIdeaId={previewId}
              setCurrentIdeaId={setPreviewId}
              selectionMode={selectingMultipleCards ? "multi" : undefined}
              selectedIdeas={selectedIdeas}
              setSelectedIdeas={setSelectedIdeas}
              laneFilter={projectBoardLanesFilter}
            />
          ) : null}
          {!basicLoading && ideas.length === 0 && ideaViewType !== "board" ? (
            <EmptyBox>
              <h2>{t("challenge.ideas.noneTitle")}</h2>
              {challenge?.stage === "published" && canMakeIdea && (
                <Button
                  primary
                  icon="plus"
                  content={challenge?.buttonText ? challenge.buttonText : t("challenge.ideas.createYourIdea")}
                  onClick={() => newIdea()}
                />
              )}
            </EmptyBox>
          ) : null}
          {!basicLoading && ideas.length > 0 && ideaViewType !== "board" ? (
            <Pagination
              limit={limit}
              limitValues={listView ? [15, 30, 50, 100] : [16, 32, 52, 100]}
              page={page}
              total={total}
              updateFilters={updateFilters}
            />
          ) : null}
        </StyledIdeaItemsContainer>
      ) : null}
    </StyledIdeasContainer>
  )
}

export default Ideas
